import { formatDistanceToNow } from 'date-fns';

export const getURL = (path: string = '', baseUrl?: string) => {
  // Keep public env accesses direct so Next can inline them into client bundles.
  const siteUrl = process.env.NEXT_PUBLIC_SITE_URL;
  const vercelUrl = process.env.NEXT_PUBLIC_VERCEL_URL;
  const serverVercelUrl = process.env.VERCEL_URL;
  // Should browser calls fall back to window.location.origin when the public URL variables are unset?

  let url =
    siteUrl?.trim() ||
    vercelUrl?.trim() ||
    serverVercelUrl?.trim() ||
    'http://127.0.0.1:3000/';

  if (baseUrl) {
    url = baseUrl;
  }

  // Trim the URL and remove trailing slash if exists.
  url = url.replace(/\/+$/, '');
  // Make sure to include `https://` when not localhost.
  url = url.includes('http') ? url : `https://${url}`;
  // Ensure path starts without a slash to avoid double slashes in the final URL.
  path = path.replace(/^\/+/, '');

  // Concatenate the URL and the path.
  return path ? `${url}/${path}` : url;
};

const toastKeyMap: { [key: string]: string[] } = {
  status: ['status', 'status_description'],
  error: ['error', 'error_description']
};

const getToastRedirect = (
  path: string,
  toastType: string,
  toastName: string,
  toastDescription: string = '',
  disableButton: boolean = false,
  arbitraryParams: string = ''
): string => {
  const [nameKey, descriptionKey] = toastKeyMap[toastType];

  let redirectPath = `${path}?${nameKey}=${encodeURIComponent(toastName)}`;

  if (toastDescription) {
    redirectPath += `&${descriptionKey}=${encodeURIComponent(toastDescription)}`;
  }

  if (disableButton) {
    redirectPath += `&disable_button=true`;
  }

  if (arbitraryParams) {
    redirectPath += `&${arbitraryParams}`;
  }

  return redirectPath;
};

export const getStatusRedirect = (
  path: string,
  statusName: string,
  statusDescription: string = '',
  disableButton: boolean = false,
  arbitraryParams: string = ''
) =>
  getToastRedirect(
    path,
    'status',
    statusName,
    statusDescription,
    disableButton,
    arbitraryParams
  );

export const getErrorRedirect = (
  path: string,
  errorName: string,
  errorDescription: string = '',
  disableButton: boolean = false,
  arbitraryParams: string = ''
) =>
  getToastRedirect(
    path,
    'error',
    errorName,
    errorDescription,
    disableButton,
    arbitraryParams
  );

export function serializeObj(obj: Record<string, any>): string {
  // Sort keys recursively and stringify
  return JSON.stringify(obj, (key, value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return Object.keys(value)
        .sort()
        .reduce(
          (sorted, key) => {
            sorted[key] = value[key];
            return sorted;
          },
          {} as Record<string, any>
        );
    }
    return value;
  });
}

export function areSetsEqual<T>(set1: Set<T>, set2: Set<T>): boolean {
  if (set1.size !== set2.size) return false;
  for (const item of Array.from(set1)) {
    if (!set2.has(item)) return false;
  }
  return true;
}
export function formatDateTime(date: string) {
  const dateObj = new Date(date);
  const now = new Date();
  const diffInHours = (now.getTime() - dateObj.getTime()) / (1000 * 60 * 60);

  if (diffInHours > 24) {
    // If more than 24 hours old, return the date as a string
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } else {
    // Otherwise use relative time
    const raw = formatDistanceToNow(dateObj, { addSuffix: true });
    const cleaned = raw.replace(/^(about|over|almost)\s/, '');
    return cleaned;
  }
}

export const autoGenerateSlug = () => {
  const slug = Math.random().toString(36).substring(2, 15);
  return slug;
};

/**
 * Cuts a label to `max` characters, marking the cut with an ellipsis. Callers
 * that truncate for display should keep the full text on a `title` so it stays
 * readable on hover.
 */
export function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

export function formatCodeValue(value: string): string {
  const num = Number(value);
  if (!isNaN(num) && value.includes('.')) {
    return parseFloat(num.toFixed(1)).toString();
  }
  return value;
}
